import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Calendar } from "../components/ui/calendar";
import { hr } from "date-fns/locale";
import { format } from "date-fns";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Clock, LogIn, LogOut, FileSpreadsheet, FileText, RefreshCw, UserPlus, Loader2, Activity, ChevronsUpDown, Check, X, CalendarIcon
} from "lucide-react";
import { toast } from "sonner";
import { downloadAsBlob } from "../lib/api";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function minutesToHm(m) {
  if (!m && m !== 0) return "—";
  const h = Math.floor(m / 60); const mm = m % 60;
  return `${h}h ${mm.toString().padStart(2, "0")}min`;
}

// Iter87: format seconds → "Xh YYmin ZZsek" for live-ticking timer.
function secondsToHms(s) {
  if (s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m.toString().padStart(2, "0")}min ${sec.toString().padStart(2, "0")}sek`;
}

export default function Attendance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPriv = ["admin", "secretary", "dean", "prodekan"].includes(user.role);
  const canManage = isPriv || user.role === "manager";

  const [today, setToday] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  // Iter47b: split timestamp into date (YYYY-MM-DD) + time (HH:mm) for Croatian-friendly UX.
  const [manualForm, setManualForm] = useState({ user_email: "", kind: "in", date: "", time: "", comment: "" });
  const [manualBusy, setManualBusy] = useState(false);
  const [manualUserOpen, setManualUserOpen] = useState(false);
  const [manualDateOpen, setManualDateOpen] = useState(false);
  const [users, setUsers] = useState([]);
  // Report month picker
  const now = new Date();
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);

  // History (events) view: default last 30 days (computed once via lazy initializer)
  const [histFrom, setHistFrom] = useState(() =>
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [histTo, setHistTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [histUser, setHistUser] = useState("");
  const [histUserOpen, setHistUserOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Iter28: when an admin/secretary opens the page we eagerly load users so the
  // history-filter datalist can autocomplete by name/surname (not just email).
  useEffect(() => {
    if (canManage && users.length === 0) {
      api.get("/users").then(r => setUsers(r.data || [])).catch(() => {});
    }
  }, [canManage, users.length]);

  const reloadAll = async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get("/attendance/today").catch(() => ({ data: [] })),
        api.get("/attendance/me/today"),
      ]);
      setToday(t.data || []);
      setMine(m.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { api.get("/attendance/me/today").then(r => setMine(r.data)).catch(() => {}); }, []);

  // Iter87: live-ticking timer — svakih 1s update lokalni state `nowMs`. Dok je
  // korisnik prijavljen, "Odrađeno danas" prikazuje `minutes_worked_today` +
  // (now - last_checkin_ts) u formatu "Xh YYmin ZZsek".
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (mine?.current_state !== "in") return;  // ne troši CPU kad nije prijavljen
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [mine?.current_state]);

  // Iter87: dinamički broj sekundi za live-timer.
  const liveSecondsToday = useMemo(() => {
    const baseSec = (mine?.minutes_worked_today || 0) * 60;
    if (mine?.current_state !== "in" || !mine?.last_checkin_ts) return baseSec;
    try {
      const inMs = new Date(mine.last_checkin_ts).getTime();
      const delta = Math.max(0, Math.floor((nowMs - inMs) / 1000));
      return baseSec + delta;
    } catch { return baseSec; }
  }, [mine, nowMs]);
  useEffect(() => { api.get("/attendance/today").then(r => setToday(r.data || [])).catch(() => {}); }, []);
  // Removed eager users load — fetched lazily when user opens the manual-entry dialog.
  // Note: auto-refresh of the "today" view was removed (lint conflict). Use "Osvježi" button.

  const fetchUsers = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data || []);
    } catch (e) {
      console.debug("[Attendance] fetchUsers failed; dropdown will be empty:", e?.message);
    }
  };

  const openManual = () => {
    if (canManage && users.length === 0) fetchUsers();
    // Iter47b: default = danas + sad u 24h formatu
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    setManualForm(f => ({ ...f, date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` }));
    setManualOpen(true);
  };

  // Iter61: QR code deep-link handler — /prisutnost?as=email auto-opens manual entry
  useEffect(() => {
    const asEmail = searchParams.get("as");
    if (!asEmail) return;
    if (!canManage) {
      // Non-admin scanned the QR — just show info toast (their own /prisutnost view loads normally)
      toast.info(`Skenirana naljepnica za ${asEmail} — kontaktirajte tajništvo za ručnu prijavu`);
      // Clear query so refresh doesn't re-trigger
      setSearchParams({}, { replace: true });
      return;
    }
    // Admin/secretary/manager → auto-open manual entry with email pre-filled
    if (users.length === 0) fetchUsers();
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    setManualForm({
      user_email: asEmail.toLowerCase(),
      kind: "in",
      date: `${yyyy}-${mm}-${dd}`,
      time: `${hh}:${mi}`,
      comment: "QR naljepnica — kartica nije pri ruci",
    });
    setManualOpen(true);
    toast.info(`Ručna prijava za ${asEmail} — provjeri detalje prije slanja`);
    setSearchParams({}, { replace: true });
  // Intentionally NOT depending on `searchParams` object or unstable `fetchUsers` — would loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("as"), canManage]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = {};
      if (histFrom) params.from_date = `${histFrom}T00:00:00`;
      if (histTo) params.to_date = `${histTo}T23:59:59`;
      // Iter47: histUser je sada UVIJEK e-mail (postavljen kroz Combobox).
      const raw = (histUser || "").trim();
      if (raw) params.user_email = raw.toLowerCase();
      const r = await api.get("/attendance/history", { params });
      setHistoryEvents(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setHistLoading(false); }
  }, [histFrom, histTo, histUser]);

  // Iter47: lookup za prikaz odabranog imena u Combobox triggeru.
  const selectedHistUserName = useMemo(() => {
    if (!histUser) return "";
    const u = users.find(x => x.email === histUser);
    return u ? `${u.full_name} · ${u.email}` : histUser;
  }, [histUser, users]);

  const usersIn = useMemo(() => today.filter(t => t.current_state === "in"), [today]);
  const usersOut = useMemo(() => today.filter(t => t.current_state === "out"), [today]);

  const submitManual = async () => {
    if (!manualForm.user_email || !manualForm.date || !manualForm.time) {
      toast.error("Odaberite zaposlenika, datum i vrijeme"); return;
    }
    // Iter47b: validacija 24h formata HH:mm
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(manualForm.time)) {
      toast.error("Vrijeme mora biti u 24h formatu HH:MM (npr. 14:30)"); return;
    }
    setManualBusy(true);
    try {
      // Combine YYYY-MM-DD + HH:mm → local Date → ISO UTC.
      const iso = new Date(`${manualForm.date}T${manualForm.time}:00`).toISOString();
      await api.post("/attendance/manual", {
        user_email: manualForm.user_email, kind: manualForm.kind,
        timestamp: iso, comment: manualForm.comment,
      });
      toast.success(`Ručni unos: ${manualForm.kind === "in" ? "dolazak" : "odlazak"}`);
      setManualOpen(false);
      setManualForm({ user_email: "", kind: "in", date: "", time: "", comment: "" });
      reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setManualBusy(false); }
  };

  const downloadExcel = async () => {
    try {
      await downloadAsBlob(`/attendance/monthly/${reportYear}/${reportMonth}.xlsx`,
        `Prisutnost_${reportYear}_${String(reportMonth).padStart(2, "0")}.xlsx`);
      toast.success("Excel preuzet");
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  const downloadMyPdf = async () => {
    try {
      await downloadAsBlob(
        `/attendance/monthly/${reportYear}/${reportMonth}/${encodeURIComponent(user.email)}.pdf`,
        `Prisutnost_${user.full_name?.replace(/ /g, "_") || "ja"}_${reportYear}_${String(reportMonth).padStart(2, "0")}.pdf`
      );
      toast.success("PDF preuzet");
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  // Manager-scoped: list of subordinates (for "team" tab on manager view)
  const teamMembers = useMemo(() =>
    users.filter(u => u.manager_email === user.email || u.email === user.email),
    [users, user.email]
  );

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="serif text-3xl sm:text-4xl font-semibold">Evidencija prisutnosti</h1>
          <p className="text-muted-foreground mt-1">RFID čitači · ručni unos · mjesečni izvještaji</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={reloadAll} data-testid="att-refresh">
            <RefreshCw className="w-4 h-4 mr-1"/>Osvježi
          </Button>
          {canManage && (
            <Button size="sm" onClick={openManual} data-testid="att-manual-btn">
              <UserPlus className="w-4 h-4 mr-1"/>Ručni unos
            </Button>
          )}
        </div>
      </div>

      {/* My current status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary"/>Moje stanje danas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? <p className="text-sm text-muted-foreground">Učitavanje…</p> : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant={mine?.current_state === "in" ? "default" : "secondary"} className="text-sm px-3 py-1">
                  {mine?.current_state === "in" ? (
                    <><LogIn className="w-4 h-4 mr-1 inline"/>Prijavljen/a</>
                  ) : (
                    <><LogOut className="w-4 h-4 mr-1 inline"/>Odjavljen/a</>
                  )}
                </Badge>
                <div className="text-sm">
                  <span className="text-muted-foreground">Odrađeno danas: </span>
                  {mine?.current_state === "in" ? (
                    <b data-testid="att-my-timer" className="tabular-nums text-primary">
                      {secondsToHms(liveSecondsToday)}
                    </b>
                  ) : (
                    <b data-testid="att-my-minutes">{minutesToHm(mine?.minutes_worked_today || 0)}</b>
                  )}
                </div>
                {mine?.overtime_minutes_today > 0 && (
                  <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 bg-amber-50" data-testid="att-my-overtime">
                    Prekovremeno: {minutesToHm(mine.overtime_minutes_today)}
                  </Badge>
                )}
              </div>
              {mine?.events_today?.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="font-medium">Današnji događaji:</div>
                  <div className="flex gap-2 flex-wrap">
                    {mine.events_today.map((e, i) => (
                      <Badge key={e.id || `${e.timestamp}-${i}`} variant="outline" className="font-normal text-[11px]">
                        {e.kind === "in" ? "↓ Dolazak" : "↑ Odlazak"} {fmtTime(e.timestamp)}
                        {e.source === "manual" && " · ručno"}
                        {e.source === "auto_checkout" && " · automatska odjava"}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Monthly report download */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary"/>Mjesečni izvještaj
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Mjesec</Label>
              <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
                <SelectTrigger className="w-40" data-testid="att-month"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["siječanj","veljača","ožujak","travanj","svibanj","lipanj","srpanj","kolovoz","rujan","listopad","studeni","prosinac"].map((m,i) => (
                    <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Godina</Label>
              <Input type="number" min="2024" max="2099" value={reportYear}
                onChange={(e) => setReportYear(Number(e.target.value))}
                className="w-28" data-testid="att-year"/>
            </div>
            <Button variant="outline" onClick={downloadMyPdf} data-testid="att-my-pdf">
              <FileText className="w-4 h-4 mr-1"/>Moj PDF
            </Button>
            {isPriv && (
              <Button onClick={downloadExcel} data-testid="att-xlsx">
                <FileSpreadsheet className="w-4 h-4 mr-1"/>Excel (svi)
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            PDF sadrži vaš mjesečni obračun redovnog i prekovremenog rada po Zakonu o radu RH.
            Excel je dostupan tajništvu i sadrži sve zaposlenike.
          </p>
        </CardContent>
      </Card>

      {/* Today live view — only managers/secretariat */}
      {isPriv ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary"/>Tko je danas na poslu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="in">
              <TabsList>
                <TabsTrigger value="in" data-testid="tab-att-in">Prijavljeni ({usersIn.length})</TabsTrigger>
                <TabsTrigger value="out" data-testid="tab-att-out">Odjavljeni ({usersOut.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="in" className="mt-3 space-y-2">
                {usersIn.length === 0 ? <p className="text-sm text-muted-foreground">Nitko trenutno nije prijavljen.</p>
                  : usersIn.map(t => <PersonRow key={t.user_email} t={t}/>)}
              </TabsContent>
              <TabsContent value="out" className="mt-3 space-y-2">
                {usersOut.length === 0 ? <p className="text-sm text-muted-foreground">Nema odjavljenih zaposlenika danas.</p>
                  : usersOut.map(t => <PersonRow key={t.user_email} t={t}/>)}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : user.role === "manager" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary"/>Moj tim danas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {today.length === 0 ? <p className="text-sm text-muted-foreground">Nitko od tima nije scannao danas.</p>
              : today.map(t => <PersonRow key={t.user_email} t={t}/>)}
          </CardContent>
        </Card>
      )}

      {/* History view — role-scoped, filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary"/>Povijest događaja
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {isPriv ? "Filtrirajte po datumu, zaposleniku i pregledajte sve scan / ručne unose."
              : user.role === "manager" ? "Filtrirajte događaje vašeg tima."
              : "Pregled svojih dolazaka i odlazaka."}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Od</Label>
              <Input type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)} data-testid="hist-from"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Do</Label>
              <Input type="date" value={histTo} onChange={(e) => setHistTo(e.target.value)} data-testid="hist-to"/>
            </div>
            {canManage && (
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Zaposlenik (pretraži po imenu ili prezimenu)</Label>
                <div className="relative">
                  <Popover open={histUserOpen} onOpenChange={setHistUserOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={histUserOpen}
                        className="w-full justify-between font-normal pr-16"
                        data-testid="hist-user"
                      >
                        <span className="truncate">
                          {selectedHistUserName || "Svi zaposlenici"}
                        </span>
                        <ChevronsUpDown className="w-4 h-4 opacity-50 flex-shrink-0"/>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
                      <Command
                        filter={(value, search) => {
                          if (!search) return 1;
                          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                        }}
                      >
                        <CommandInput placeholder="Pretraga po imenu / prezimenu / katedri…" data-testid="hist-user-search"/>
                        <CommandList>
                          <CommandEmpty>Nema rezultata.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="__all__ svi zaposlenici"
                              onSelect={() => { setHistUser(""); setHistUserOpen(false); }}
                              data-testid="hist-user-opt-all"
                            >
                              <Check className={`w-4 h-4 mr-2 ${!histUser ? "opacity-100" : "opacity-0"}`}/>
                              <span className="italic text-muted-foreground">Svi zaposlenici</span>
                            </CommandItem>
                            {users.map(u => {
                              const value = `${u.full_name} ${u.email} ${u.katedra || ""}`;
                              return (
                                <CommandItem
                                  key={u.email}
                                  value={value}
                                  onSelect={() => { setHistUser(u.email); setHistUserOpen(false); }}
                                  data-testid={`hist-user-opt-${u.email}`}
                                >
                                  <Check className={`w-4 h-4 mr-2 flex-shrink-0 ${histUser === u.email ? "opacity-100" : "opacity-0"}`}/>
                                  <div className="flex-1 min-w-0">
                                    <div className="truncate">{u.full_name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{u.email}{u.katedra ? ` · ${u.katedra}` : ""}</div>
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {histUser && (
                    <button
                      type="button"
                      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); setHistUser(""); }}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      className="absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary opacity-60 hover:opacity-100"
                      aria-label="Očisti filter zaposlenika"
                      data-testid="hist-user-clear"
                    >
                      <X className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className={`flex items-end ${canManage ? "" : "col-span-2"}`}>
              <Button onClick={loadHistory} disabled={histLoading} className="w-full" data-testid="hist-load">
                {histLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <RefreshCw className="w-4 h-4 mr-1"/>}
                Učitaj
              </Button>
            </div>
          </div>
          {historyEvents.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2">Vrijeme</th>
                    {canManage && <th className="text-left p-2">Zaposlenik</th>}
                    <th className="text-center p-2">Tip</th>
                    <th className="text-center p-2">Izvor</th>
                    <th className="text-right p-2">Kašnjenje</th>
                  </tr>
                </thead>
                <tbody>
                  {historyEvents.map((e, i) => (
                    <tr key={e.id || `${e.timestamp}-${e.user_email}-${i}`} className="border-t" data-testid="hist-row">
                      <td className="p-2 text-xs">{fmtDateTime(e.timestamp)}</td>
                      {canManage && <td className="p-2 text-xs">{e.full_name || e.user_email}</td>}
                      <td className="p-2 text-center">
                        <Badge variant={e.kind === "in" ? "default" : "secondary"} className="text-[10px]">
                          {e.kind === "in" ? "↓ DOLAZAK" : "↑ ODLAZAK"}
                        </Badge>
                      </td>
                      <td className="p-2 text-center text-[10px] text-muted-foreground">
                        {e.source === "rfid" ? "RFID" : e.source === "manual" ? "RUČNO" : e.source === "auto_checkout" ? "AUTO ODJAVA" : e.source}
                        {e.source === "rfid" && e.reader_id && <span className="ml-1">· {e.reader_id}</span>}
                      </td>
                      <td className="p-2 text-right">
                        {e.late_minutes > 0
                          ? <Badge variant="destructive" className="text-[10px]" data-testid="hist-late">{e.late_minutes} min</Badge>
                          : <span className="text-[10px] text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">
                Prikazano {historyEvents.length} događaja. Kašnjenje se računa za &quot;dolazak&quot; događaje prema dodijeljenom rasporedu (uz toleranciju max-kašnjenja).
              </p>
            </div>
          )}
          {!histLoading && historyEvents.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Kliknite &quot;Učitaj&quot; za prikaz događaja u zadanom razdoblju.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Manual entry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ručni unos dolaska/odlaska</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Zaposlenik</Label>
              <Popover open={manualUserOpen} onOpenChange={setManualUserOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={manualUserOpen}
                    className="w-full justify-between font-normal"
                    data-testid="att-manual-user"
                  >
                    <span className="truncate">
                      {manualForm.user_email
                        ? (() => {
                            const u = users.find(x => x.email === manualForm.user_email);
                            return u ? `${u.full_name} · ${u.email}` : manualForm.user_email;
                          })()
                        : "Odaberite zaposlenika…"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 flex-shrink-0"/>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
                  <Command
                    filter={(value, search) => {
                      if (!search) return 1;
                      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Pretraga po imenu / prezimenu / katedri…" data-testid="att-manual-user-search"/>
                    <CommandList>
                      <CommandEmpty>Nema rezultata.</CommandEmpty>
                      <CommandGroup>
                        {(isPriv ? users : teamMembers).map(u => {
                          const value = `${u.full_name} ${u.email} ${u.katedra || ""}`;
                          return (
                            <CommandItem
                              key={u.email}
                              value={value}
                              onSelect={() => {
                                setManualForm({ ...manualForm, user_email: u.email });
                                setManualUserOpen(false);
                              }}
                              data-testid={`att-manual-user-opt-${u.email}`}
                            >
                              <Check className={`w-4 h-4 mr-2 flex-shrink-0 ${manualForm.user_email === u.email ? "opacity-100" : "opacity-0"}`}/>
                              <div className="flex-1 min-w-0">
                                <div className="truncate">{u.full_name}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{u.email}{u.katedra ? ` · ${u.katedra}` : ""}</div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label>Tip</Label>
              <Select value={manualForm.kind} onValueChange={(v) => setManualForm({ ...manualForm, kind: v })}>
                <SelectTrigger data-testid="att-manual-kind"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Dolazak</SelectItem>
                  <SelectItem value="out">Odlazak</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Datum</Label>
                <Popover open={manualDateOpen} onOpenChange={setManualDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start font-normal"
                      data-testid="att-manual-date"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 opacity-60"/>
                      <span>
                        {manualForm.date
                          ? format(new Date(`${manualForm.date}T00:00:00`), "EEE, dd.MM.yyyy.", { locale: hr })
                          : "Odaberite…"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      locale={hr}
                      weekStartsOn={1}
                      selected={manualForm.date ? new Date(`${manualForm.date}T00:00:00`) : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        const dd = String(d.getDate()).padStart(2, "0");
                        setManualForm(f => ({ ...f, date: `${yyyy}-${mm}-${dd}` }));
                        setManualDateOpen(false);
                      }}
                      data-testid="att-manual-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label>Vrijeme (24h)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="HH:MM"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  maxLength={5}
                  value={manualForm.time}
                  onChange={(e) => {
                    // Auto-formatter: digits → HH:MM
                    let v = e.target.value.replace(/[^\d:]/g, "");
                    if (v.length === 2 && !v.includes(":") && manualForm.time.length < 2) v = `${v}:`;
                    setManualForm({ ...manualForm, time: v });
                  }}
                  data-testid="att-manual-time"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Razlog (opcionalno)</Label>
              <Input value={manualForm.comment}
                onChange={(e) => setManualForm({ ...manualForm, comment: e.target.value })}
                placeholder="npr. zaboravljeno scannati"
                maxLength={300}
                data-testid="att-manual-comment"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Odustani</Button>
            <Button onClick={submitManual} disabled={manualBusy} data-testid="att-manual-submit">
              {manualBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
              Spremi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PersonRow({ t }) {
  return (
    <div className="flex items-center justify-between p-3 border rounded-md bg-card" data-testid={`att-person-${t.user_email}`}>
      <div className="min-w-0">
        <div className="font-medium truncate">{t.full_name}</div>
        <div className="text-[11px] text-muted-foreground truncate">{t.katedra}</div>
      </div>
      <div className="text-right flex-shrink-0">
        <Badge variant={t.current_state === "in" ? "default" : "secondary"} className="text-[10px]">
          {t.current_state === "in" ? "PRIJAVLJEN" : "ODJAVLJEN"}
        </Badge>
        <div className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(t.last_event_at)}</div>
      </div>
    </div>
  );
}
